import React from 'react';
import { Button, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PhotoCameraOutlinedIcon from '@material-ui/icons/PhotoCameraOutlined';
// `user-default-image.png` is a blank placeholder asset, so we reuse the same
// default avatar used elsewhere in the app (e.g. the header's user menu).
import userDefaultImage from '../../Assets/images/customer-default-logo.png';

const useStyles = makeStyles((theme) => ({
    panel: {
        display: 'flex',
        alignItems: 'center',
        gap: 20,
        width: '100%',
        boxSizing: 'border-box',
        padding: '16px 20px',
        background: 'var(--dm-row-alt, #f5f8fc)',
        border: '1px solid var(--dm-border, #e7ebf0)',
        borderRadius: 12,
        [theme.breakpoints.down('xs')]: {
            flexDirection: 'column',
            alignItems: 'flex-start',
        },
    },
    compact: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(1),
        width: '100%',
        boxSizing: 'border-box',
        padding: theme.spacing(2, 1.5),
        background: 'var(--dm-row-alt, #f8fafc)',
        border: '1px dashed var(--dm-border, #d5dde8)',
        borderRadius: 12,
        textAlign: 'center',
        [theme.breakpoints.down('xs')]: {
            flexDirection: 'row',
            textAlign: 'left',
            padding: theme.spacing(1.5),
        },
    },
    avatarWrap: {
        position: 'relative',
        flexShrink: 0,
    },
    avatar: {
        width: 72,
        height: 72,
        borderRadius: '50%',
        objectFit: 'cover',
        background: 'var(--contentColor, #fff)',
        border: '1px solid var(--dm-border, #e7ebf0)',
        flexShrink: 0,
        display: 'block',
    },
    avatarCompact: {
        width: 88,
        height: 88,
        boxShadow: '0 4px 14px rgba(12, 41, 84, 0.08)',
        [theme.breakpoints.down('xs')]: {
            width: 64,
            height: 64,
        },
    },
    cameraBadge: {
        position: 'absolute',
        right: -2,
        bottom: -2,
        width: 30,
        height: 30,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        color: '#fff',
        backgroundColor: theme.palette.primary.main,
        border: '2px solid var(--contentColor, #fff)',
        '& svg': { fontSize: 16 },
        '&:focus-within, &:hover': { backgroundColor: theme.palette.primary.dark },
    },
    copy: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 4,
        minWidth: 0,
    },
    copyCompact: {
        alignItems: 'center',
        [theme.breakpoints.down('xs')]: { alignItems: 'flex-start' },
    },
    title: {
        fontWeight: 600,
        fontSize: 14,
        lineHeight: 1.3,
        color: 'var(--dm-text-strong, #263548)',
    },
    hint: {
        color: 'var(--dm-muted, #647184)',
        fontSize: 12,
        lineHeight: 1.4,
    },
    actions: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: 4,
        marginTop: 2,
    },
    fileInput: {
        display: 'none',
    },
    uploadBtn: {
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 8,
        padding: '4px 12px',
        boxShadow: 'none',
    },
    removeBtn: {
        textTransform: 'none',
        fontWeight: 600,
        padding: '4px 8px',
        minWidth: 0,
        color: 'var(--dm-muted, #647184)',
    },
}));

/**
 * `variant="panel"` is a full-width row (profile page); `variant="compact"` is a small
 * card meant to sit beside form fields.
 */
export default function ProfilePhotoField({
    src,
    inputId,
    onChange,
    onRemove,
    accept = 'image/jpeg,image/png',
    title = 'Profile photo',
    hint,
    buttonLabel,
    variant = 'panel',
}) {
    const classes = useStyles();
    const hasPhoto = Boolean(src);
    const compact = variant === 'compact';
    const hintText = hint ?? (compact ? 'JPEG or PNG' : '');

    return (
        <div className={compact ? classes.compact : classes.panel}>
            <input
                id={inputId}
                className={classes.fileInput}
                accept={accept}
                type="file"
                onChange={onChange}
            />
            <div className={classes.avatarWrap}>
                <img
                    loading="lazy"
                    decoding="async"
                    className={`${classes.avatar}${compact ? ` ${classes.avatarCompact}` : ''}`}
                    src={hasPhoto ? src : userDefaultImage}
                    alt={hasPhoto ? 'Profile photo' : 'No profile photo yet'}
                />
                {compact ? (
                    <label htmlFor={inputId} className={classes.cameraBadge} title={hasPhoto ? 'Change photo' : 'Add photo'}>
                        <PhotoCameraOutlinedIcon aria-hidden="true" />
                    </label>
                ) : null}
            </div>
            <div className={`${classes.copy}${compact ? ` ${classes.copyCompact}` : ''}`}>
                <Typography className={classes.title}>{title}</Typography>
                {hintText ? <Typography className={classes.hint}>{hintText}</Typography> : null}
                <div className={classes.actions}>
                    <label htmlFor={inputId}>
                        <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            component="span"
                            className={classes.uploadBtn}
                            disableElevation
                        >
                            {buttonLabel || (hasPhoto ? 'Change photo' : 'Add photo')}
                        </Button>
                    </label>
                    {hasPhoto && onRemove ? (
                        <Button size="small" className={classes.removeBtn} onClick={onRemove}>
                            Remove
                        </Button>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
